/**
 * Blog Service
 * This service handles communication with the Azure Functions backend for blog management.
 */

import axios from 'axios';

class BlogService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE || 'http://localhost:7071/api';
    this.baseURL = this.baseURL.endsWith('/') ? this.baseURL.slice(0, -1) : this.baseURL;
  }

  /**
   * Format numbers with K, M abbreviations (1000 -> 1K, 1100 -> 1.1K)
   * @param {number} num - Number to format
   * @returns {string} - Formatted number string
   */
  formatNumber(num) {
    if (!num || num < 1000) return num?.toString() || '0';
    
    if (num >= 1000000) {
      const millions = num / 1000000;
      return millions % 1 === 0 ? `${millions}M` : `${millions.toFixed(1)}M`;
    }
    
    if (num >= 1000) {
      const thousands = num / 1000;
      return thousands % 1 === 0 ? `${thousands}K` : `${thousands.toFixed(1)}K`;
    }
    
    return num.toString();
  }

  /**
   * Get all blogs with optional filters
   * @param {Object} filters - Optional filters
   * @returns {Promise} - API response
   */
  async getBlogs(filters = {}) {
    try {
      const response = await axios.get(`${this.baseURL}/blog/get`, { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error getting blogs:', error);
      throw error;
    }
  }

  /**
   * Get a specific blog by ID
   * @param {string} blogId - Blog ID
   * @returns {Promise} - API response
   */
  async getBlogById(blogId) {
    try {
      const response = await axios.get(`${this.baseURL}/blog/id/get?id=${blogId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting blog by ID:', error);
      throw error;
    }
  }

  /**
   * Update blog views count
   * @param {string} blogId - Blog ID
   * @param {Object} blogData - Complete blog data with updated views
   * @returns {Promise} - API response
   */
  async updateBlogViews(blogId, blogData) {
    try {
      const updatedData = {
        ...blogData,
        views: (blogData.views || 0) + 1,
        lastViewedAt: Date.now()
      };
      
      const response = await axios.post(`${this.baseURL}/blog/post?id=${blogId}`, updatedData);
      return response.data;
    } catch (error) {
      console.error('Error updating blog views:', error);
      throw error;
    }
  }

  /**
   * Update blog likes count
   * @param {string} blogId - Blog ID
   * @param {Object} blogData - Complete blog data
   * @param {boolean} isLiked - Whether the blog is being liked or unliked
   * @returns {Promise} - API response
   */
  async updateBlogLikes(blogId, blogData, isLiked) {
    try {
      const currentLikes = blogData.likes || 0;
      const updatedData = {
        ...blogData,
        likes: isLiked ? currentLikes + 1 : Math.max(0, currentLikes - 1)
      };
      
      const response = await axios.post(`${this.baseURL}/blog/post?id=${blogId}`, updatedData);
      return response.data;
    } catch (error) {
      console.error('Error updating blog likes:', error);
      throw error;
    }
  }

  /**
   * Calculate reading time for blog content
   * @param {string} content - Blog content (HTML or plain text)
   * @returns {number} - Estimated reading time in minutes
   */
  calculateReadingTime(content) {
    if (!content) return 0;
    
    // Remove HTML tags
    const plainText = content.replace(/<[^>]*>/g, '');
    
    // Count words (split by whitespace and filter empty strings)
    const words = plainText.split(/\s+/).filter(word => word.length > 0);
    const wordCount = words.length;
    
    // Average reading speed is 200-250 words per minute
    // We'll use 200 for a more conservative estimate
    const wordsPerMinute = 200;
    const readingTime = Math.ceil(wordCount / wordsPerMinute);
    
    return Math.max(1, readingTime); // Minimum 1 minute
  }

  /**
   * Generate excerpt from blog content
   * @param {string} content - Blog content
   * @param {number} maxLength - Maximum length of excerpt
   * @returns {string} - Generated excerpt
   */
  generateExcerpt(content, maxLength = 150) {
    if (!content) return '';
    
    // Remove HTML tags
    const plainText = content.replace(/<[^>]*>/g, '');
    
    // Trim and limit length
    const trimmed = plainText.trim();
    if (trimmed.length <= maxLength) return trimmed;
    
    // Find the last complete word within the limit
    const truncated = trimmed.substring(0, maxLength);
    const lastSpaceIndex = truncated.lastIndexOf(' ');
    
    if (lastSpaceIndex > maxLength * 0.8) {
      return truncated.substring(0, lastSpaceIndex) + '...';
    }
    
    return truncated + '...';
  }

  /**
   * Get blog categories
   * @returns {Promise} - API response with categories
   */
  async getBlogCategories() {
    try {
      const response = await axios.get(`${this.baseURL}/categories-blog/get`);
      return response.data;
    } catch (error) {
      console.error('Error getting blog categories:', error);
      throw error;
    }
  }

  /**
   * Format blog data with additional computed properties
   * @param {Object} blog - Raw blog data
   * @param {boolean} includeCommentCount - Whether to fetch real comment count (default: false for performance)
   * @returns {Object} - Enhanced blog data
   */
  async formatBlogData(blog, includeCommentCount = false) {
    const readingTime = this.calculateReadingTime(blog.content || blog.text);
    const excerpt = this.generateExcerpt(blog.content || blog.text, 150);
    
    // Get real comment count only if requested (for single blog view)
    let commentsCount = blog.comments || 0;
    if (includeCommentCount) {
      try {
        const commentService = (await import('./commentService')).default;
        const commentCountData = await commentService.getCommentCount(blog.id);
        commentsCount = commentCountData.totalComments || 0;
      } catch (error) {
        console.warn('Could not fetch comment count for blog:', blog.id, error);
        // Fallback to stored count
        commentsCount = blog.comments || 0;
      }
    }
    
    return {
      ...blog,
      readingTime,
      excerpt,
      formattedDate: this.formatDate(blog.createDate),
      relativeDate: this.getRelativeDate(blog.createDate),
      views: blog.views || 0,
      likes: blog.likes || 0,
      comments: commentsCount
    };
  }

  /**
   * Format blog data synchronously (for lists) - with option to fetch real comment count
   * @param {Object} blog - Raw blog data
   * @param {boolean} includeRealCommentCount - Whether to fetch real comment count from API (async)
   * @returns {Object} - Enhanced blog data
   */
  formatBlogDataSync(blog, includeRealCommentCount = false) {
    const readingTime = this.calculateReadingTime(blog.content || blog.text);
    const excerpt = this.generateExcerpt(blog.content || blog.text, 150);
    
    let commentsCount = blog.comments || 0;
    
    // If real comment count is requested, fetch it asynchronously
    if (includeRealCommentCount) {
      this.getCommentCount(blog.id).then(count => {
        // This will update the count asynchronously
        blog.realComments = count.totalComments || 0;
      }).catch(() => {
        blog.realComments = blog.comments || 0;
      });
    }
    
    return {
      ...blog,
      readingTime,
      excerpt,
      formattedDate: this.formatDate(blog.createDate),
      relativeDate: this.getRelativeDate(blog.createDate),
      views: blog.views || 0,
      likes: blog.likes || 0,
      comments: commentsCount,
      formattedViews: this.formatNumber(blog.views || 0),
      formattedLikes: this.formatNumber(blog.likes || 0),
      formattedComments: this.formatNumber(commentsCount)
    };
  }
  
  /**
   * Get comment count for a blog
   * @param {string} blogId - Blog ID
   * @returns {Promise} Comment count response
   */
  async getCommentCount(blogId) {
    try {
      const commentService = (await import('./commentService')).default;
      return await commentService.getCommentCount(blogId);
    } catch (error) {
      console.warn('Could not fetch comment count for blog:', blogId, error);
      return { totalComments: 0 };
    }
  }

  /**
   * Update a blog post
   * @param {string} blogId - Blog ID
   * @param {Object} blogData - Updated blog data
   * @returns {Promise} - API response
   */
  async updateBlog(blogId, blogData) {
    try {
      const response = await axios.put(`${this.baseURL}/blog/update?id=${blogId}`, blogData);
      return response.data;
    } catch (error) {
      console.error('Error updating blog:', error);
      throw error;
    }
  }

  /**
   * Delete a blog post
   * @param {string} blogId - Blog ID
   * @returns {Promise} - API response
   */
  async deleteBlog(blogId) {
    try {
      const response = await axios.delete(`${this.baseURL}/blog/delete?id=${blogId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting blog:', error);
      throw error;
    }
  }

  /**
   * Format date to readable string
   * @param {string|number} date - Date to format
   * @returns {string} - Formatted date
   */
  formatDate(date) {
    if (!date) return '';
    
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('ro-RO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  /**
   * Get relative date string
   * @param {string|number} date - Date to format
   * @returns {string} - Relative date string
   */
  getRelativeDate(date) {
    if (!date) return '';
    
    const now = new Date();
    const dateObj = new Date(date);
    const diffInMs = now - dateObj;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Astăzi';
    if (diffInDays === 1) return 'Ieri';
    if (diffInDays < 7) return `Acum ${diffInDays} zile`;
    if (diffInDays < 30) return `Acum ${Math.floor(diffInDays / 7)} săptămâni`;
    if (diffInDays < 365) return `Acum ${Math.floor(diffInDays / 30)} luni`;
    
    return `Acum ${Math.floor(diffInDays / 365)} ani`;
  }
}

const blogService = new BlogService();
export default blogService;
